import { useEffect } from "react";
import { isTextEntryTarget } from "../utils/keyboardTarget";
import { SELECTABLE_SELECTOR } from "../utils/selectable";

type SelectionKeyboardOptions = {
  /** When false, no listener is attached (e.g. before the items have loaded). */
  enabled: boolean;
  /** Cmd/Ctrl+A — select every item. */
  onSelectAll: () => void;
  /** Escape — clear the selection. */
  onClear: () => void;
};

/**
 * Window-level keyboard shortcuts for a selectable file browser:
 *  - Cmd/Ctrl+A selects every item,
 *  - Escape clears the selection and drops focus off any focused item.
 *
 * Suppressed while a preview modal is open (a `.MuiModal-root` exists) so the
 * modal keeps ownership of those keys, and while focus is in a text field
 * (`input`/`textarea`/`contentEditable`) so typing isn't hijacked.
 */
export function useSelectionKeyboard({
  enabled,
  onSelectAll,
  onClear,
}: SelectionKeyboardOptions): void {
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (document.querySelector(".MuiModal-root")) return;

      if (isTextEntryTarget(e.target)) return;

      if ((e.metaKey || e.ctrlKey) && (e.key === "a" || e.key === "A")) {
        e.preventDefault();
        onSelectAll();
      } else if (e.key === "Escape") {
        onClear();
        const active = document.activeElement as HTMLElement | null;
        if (active?.matches(SELECTABLE_SELECTOR)) active.blur();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled, onSelectAll, onClear]);
}
